// now using Long.js because bitwise operations in JS automatically convert to 32bit, which was rounding UNIX timestamps
// https://github.com/dcodeIO/long.js
import turfDistance from '@turf/distance';
import { type Feature, featureCollection, type MultiLineString, multiLineString, point } from '@turf/helpers';
import type GeoJSON from 'geojson';
import Long from 'long';
import { ElevationPoint, LatLngComplete } from '../types/Geo';

/*
 * Actively work to destroy this code. It is carried over for our specific
 * needs when deserializing activities from the API into geoJSON. This is all
 * directly copied from the alltrails monolith. We should lobby to make API
 * changes _if_ that might help clean up some of the #frontend work involved
 * below.
 *
 * DO NOT ADD TO THIS CODE. ACTIVELY WORK TO TEAR AWAY AT THIS CODE.
 */

const buildAtMapProps = (atMap: any) => {
  const { ID, id, created_at, metadata, name, presentationType, slug, trail_id, trailId, type } = atMap;
  return {
    ID: ID || id,
    created_at: metadata ? metadata.created : created_at,
    isVerifiedMap: type === 'AtVerifiedMap',
    name,
    slug,
    trailId: trailId || trail_id,
    type: presentationType || 'track'
  };
};

const getLineColor = (atLine: any) => {
  const defaultLineColor = '#ff0000';
  if (!atLine) {
    return defaultLineColor;
  }
  return atLine.lineDisplayProperty ? atLine.lineDisplayProperty.color : defaultLineColor;
};

const lineOrSegSort = (a: any, b: any) => {
  if (a.sequence_num && b.sequence_num) return a.sequence_num - b.sequence_num;
  if (a.sequenceNum && b.sequenceNum) return a.sequenceNum - b.sequenceNum;
  return a.id - b.id;
};

const getLines = (atMap: any) => {
  if (!atMap) {
    return [];
  }
  const { routes, tracks } = atMap;
  const lines = routes && routes.length > 0 ? routes : tracks;
  if (!lines) {
    return [];
  }
  return [...lines].sort(lineOrSegSort);
};

const getSegments = (atLine: any) => {
  if (!atLine) {
    return [];
  }
  const { lineSegments, lineTimedSegments } = atLine;
  const segments = lineSegments && lineSegments.length > 0 ? lineSegments : lineTimedSegments;
  if (!segments) {
    return [];
  }
  return [...segments].sort(lineOrSegSort);
};

// Mapbox may do this for us - verify changing the implementation doesn't break
// things first, but maybe pursue https://github.com/mapbox/polyline
const decodedObjectsFromPolyline = (chars: string, resolutions: number[]) => {
  const { length } = chars;
  const dimensions = resolutions.length;

  let count = 0;
  let shift = 0;
  let value = Long.ZERO;
  let bbyte = Long.ZERO;

  const buffer = [dimensions * length, 0];
  let bufferCount = 0;

  while (count < length) {
    bbyte = Long.ZERO;

    for (let i = 0; i < dimensions; i += 1) {
      value = Long.ZERO;
      shift = 0;
      do {
        if (count >= length) {
          break;
        }
        bbyte = Long.fromNumber(chars.charCodeAt(count) - 63);
        count += 1;
        value = value.or(bbyte.and(0x1f).toNumber() * 2 ** shift);
        shift += 5;
      } while (bbyte.toNumber() >= 0x20);
      const delta = value.and(1).toNumber() !== 0 ? value.shiftRight(1).not() : value.shiftRight(1);
      const lastValue = bufferCount === 0 ? 0 : buffer[(bufferCount - 1) * dimensions + i];
      buffer[bufferCount * dimensions + i] =
        Math.round((lastValue + 10 ** -resolutions[i] * delta.toNumber()) * 10 ** resolutions[i]) / 10 ** resolutions[i];
    }
    bufferCount += 1;
  }

  const res: any[] = [];
  for (let index = 0, { length: bufferLength } = buffer; index < bufferLength; index += dimensions) {
    res.push(buffer.slice(index, index + dimensions));
  }
  return res;
};

const decodeLatLngs = (pointsData: string) => decodedObjectsFromPolyline(pointsData, [5, 5]);

const reverseCoord = (coord: number[]) => [coord[1], coord[0]];

const decodeSegmentLatLng = (segment: any) => {
  if (!segment || !segment.polyline) {
    return [];
  }
  const { pointsData } = segment.polyline;
  if (!pointsData || pointsData === '') {
    return [];
  }
  return decodeLatLngs(pointsData);
};

const decodeSegmentLngLat = (segment: any) => decodeSegmentLatLng(segment).map((latLng: number[]) => reverseCoord(latLng));

// Note: explore if it makes sense to combine start/end markers, photos, waypoints, etc. into single Geojson source
// Pros: easier to switch in/out atMap data
// Cons: less flexibility / modularity
// Layers would need to be filtered like ['==', $type, 'LineString']
// https://docs.mapbox.com/help/troubleshooting/mapbox-gl-js-performance/#combine-vector-tile-sources
const atMapsToGeojson = (atMaps: any[], includeProps = false) => {
  const features: Feature<MultiLineString, any>[] = [];

  atMaps.forEach((atMap: any) => {
    if (!atMap) {
      return;
    }
    let atMapProps;
    if (includeProps) {
      atMapProps = buildAtMapProps(atMap);
    }
    getLines(atMap).forEach((atLine: any) => {
      const coordinates: any[] = [];
      const color = getLineColor(atLine);
      const lineId = atLine.id;
      getSegments(atLine).forEach(segment => {
        const lngLats = decodeSegmentLngLat(segment);
        if (lngLats.length > 0) {
          // Collect segment lngLats as coordinates for multiLineString
          coordinates.push(lngLats);
        }
      });
      if (coordinates.length > 0) {
        features.push(multiLineString(coordinates, { ...atMapProps, color, lineId }));
      }
    });
  });

  return featureCollection(features);
};

type PointItem = {
  id?: number;
  ID?: string | number;
  _geoloc?: { lng: number; lat: number };
  location?: { longitude: number; latitude: number };
  latitude?: number;
  longitude?: number;
  lngLat?: number[];
};

const pointItemToLngLat = (item: PointItem): [number, number] | null => {
  let lngLat;
  if (!item) {
    return null;
  }
  if (item._geoloc) {
    // Algolia Objects
    const loc = item._geoloc;
    lngLat = [loc.lng, loc.lat];
  } else if (item.location) {
    // AllTrails Objects
    const loc = item.location;
    lngLat = [loc.longitude, loc.latitude];
  } else if (item.latitude && item.longitude) {
    // Location Objects
    lngLat = [item.longitude, item.latitude];
  } else if (item.lngLat) {
    // Object with lngLat
    lngLat = item.lngLat;
  }
  if (lngLat && (lngLat[0] || lngLat[0] === 0) && (lngLat[1] || lngLat[1] === 0)) {
    if (lngLat[0] === 0 && lngLat[1] === 0) {
      // assume items with lat = 0 and lng = 0 have no location data
      return null;
    }

    return lngLat;
  }
  // N/A
  return null;
};

const pointItemToGeojson = (item: PointItem): GeoJSON.Feature<GeoJSON.Point> | null => {
  const lngLat = pointItemToLngLat(item);
  if (lngLat) {
    const { id, ID } = item;
    return point(lngLat, item, { id: id || ID });
  }
  // N/A
  return null;
};

const pointItemsToGeojson = (items: PointItem[]) => {
  const geojsonPoints: GeoJSON.Feature<GeoJSON.Point>[] = [];
  items.forEach((item: PointItem) => {
    const p = pointItemToGeojson(item);
    if (p) {
      geojsonPoints.push(p);
    }
  });
  return featureCollection(geojsonPoints);
};

const decodeIdxElev = (idxElevData: string) => decodedObjectsFromPolyline(idxElevData, [2, 5]);

const decodeSegmentIdxElev = (segment: any) => {
  if (!segment || !segment.polyline) {
    return [];
  }
  const idxElevData = segment.polyline.indexedElevationData;
  if (!idxElevData) {
    return [];
  }
  return decodeIdxElev(idxElevData);
};

const decodeSegmentIdxElevHash = (segment: [string, number][]) => {
  const hash: { [key: string]: number } = {};
  decodeSegmentIdxElev(segment).forEach(([key, value]) => {
    hash[key] = value;
  });
  return hash;
};

const decodeSegmentIdxTimeData = (segment: any) => {
  if (!segment || !segment.polyline) {
    return [];
  }
  const idxTimeData = segment.polyline.indexedTimeData;
  if (!idxTimeData) {
    return [];
  }
  return decodedObjectsFromPolyline(idxTimeData, [2, 2]);
};

const decodeSegmentIdxTimeDataHash = (segment: [string, number][]) => {
  const hash: { [key: string]: number } = {};
  decodeSegmentIdxTimeData(segment).forEach(([key, value]) => {
    hash[key] = value;
  });
  return hash;
};

const calculate3DDistance = (horizontalDistance: number, elevationChange: number): number => {
  return Math.sqrt(horizontalDistance ** 2 + elevationChange ** 2);
};

const getIncrStats = (currNdim: number[], prevNdim: number[], use3DDistance?: boolean) => {
  const currLatLng = [currNdim[0], currNdim[1]];
  const elevation = currNdim[2];
  const elev = !elevation && elevation !== 0 ? null : elevation;
  const currentTime = currNdim[3];
  if (!prevNdim) {
    return {
      distFromLast: 0,
      elevGainFromLast: 0,
      elev,
      currLatLng,
      currentTime,
      timeFromLast: 0
    };
  }

  // Calculate distance from last point
  const currLngLat = currLatLng.slice().reverse();
  const prevLngLat = [prevNdim[1], prevNdim[0]];
  const horizontalDistance = turfDistance(prevLngLat, currLngLat, { units: 'meters' });

  // Calculate elevation gain from last point
  const currElev = currNdim[2];
  const prevElev = prevNdim[2];
  let elevGainFromLast = 0;
  if (currElev > prevElev) {
    elevGainFromLast = currElev - prevElev;
  }

  // Calculate final distance (3D if enabled and elevation data exists)
  let distFromLast = horizontalDistance;
  if (use3DDistance && (currElev || currElev === 0) && (prevElev || prevElev === 0)) {
    const elevationChange = Math.abs(currElev - prevElev);
    distFromLast = calculate3DDistance(horizontalDistance, elevationChange);
  }

  return {
    distFromLast,
    elevGainFromLast,
    elev,
    currLatLng,
    currentTime,
    timeFromLast: currentTime - prevNdim[3]
  };
};

// Group by segment for recordings, group by route for maps
// Grouping behavior defines how gaps are shown on elevation graph
const getGroupedSegments = (atMap: any) => {
  if (!atMap) {
    return [];
  }
  const isRecording = atMap.presentationType === 'track';
  const groupedSegments: { segments: any[]; color: string }[] = [];
  getLines(atMap).forEach(atLine => {
    const color = getLineColor(atLine);
    const lineSegments = getSegments(atLine).map(segment => {
      if (isRecording) {
        groupedSegments.push({
          segments: [segment],
          color
        });
      }
      return segment;
    });
    if (!isRecording && lineSegments.length > 0) {
      groupedSegments.push({
        segments: lineSegments,
        color
      });
    }
  });
  return groupedSegments;
};

const processGroupedSegments = (groupedSegments: { segments: any[]; color: string }[], shouldReturnTimeData?: boolean, use3DDistance?: boolean) => {
  const pointByPointSegmented: ElevationPoint[][] = [];
  let totalDistance = 0;
  let elevationGain = 0;
  let elapsedTime = 0;
  groupedSegments?.forEach(group => {
    const pointByPoint: ElevationPoint[] = [];
    group?.segments?.forEach?.(segment => {
      const segPoints = decodeSegmentLatLng?.(segment);
      const segIdxElevs = decodeSegmentIdxElevHash?.(segment);
      const segIdxTimeData = decodeSegmentIdxTimeDataHash?.(segment);
      segPoints?.forEach?.((curr, i) => {
        const currNdim = shouldReturnTimeData
          ? [curr[0], curr[1], segIdxElevs[i] || 0, segIdxTimeData[i] || 0]
          : [curr[0], curr[1], segIdxElevs[i] || 0];
        let prevNdim;
        if (i > 0) {
          const prev = segPoints[i - 1];
          prevNdim = shouldReturnTimeData
            ? [prev[0], prev[1], segIdxElevs[i - 1] || 0, segIdxTimeData[i - 1] || 0]
            : [prev[0], prev[1], segIdxElevs[i - 1] || 0];
        }
        const { distFromLast, elevGainFromLast, elev, currLatLng, currentTime, timeFromLast } = getIncrStats(currNdim, prevNdim, use3DDistance);
        const pointData = shouldReturnTimeData
          ? ([totalDistance, elev, currLatLng, { currentTime, elapsedTime }] as ElevationPoint)
          : ([totalDistance, elev, currLatLng] as ElevationPoint);
        // Push totalDistance, converted elevation, lat/lng, and optional currentTime/elapsedTime onto pointByPoint
        pointByPoint.push(pointData);
        // Increment distance and elevation gain counters
        totalDistance += distFromLast;
        elevationGain += elevGainFromLast;
        elapsedTime += timeFromLast;
      });
    });
    pointByPointSegmented.push(pointByPoint);
  });
  return {
    pointByPointSegmented,
    totalDistance,
    elevationGain
  };
};

const decodeNDim = (ndim: string): LatLngComplete[] => decodedObjectsFromPolyline(ndim, [5, 5, 5, 2, 5, 5]);

const encodeObjectsToPolyline = (values: number[][], cols: number, resolutions: number[]) => {
  const rows = values.length;
  const newValues = values.reduce((a, b) => a.concat(b), []);

  let value = 0;
  let output = '';
  for (let i = 0; i < rows * cols; i += 1) {
    // in 0..rows * cols-1
    value = 0;
    // Calculate the delta between the last value and the current one
    // Need to round both to the correct resolution first otherwise a rounding error drift occurs
    value = Math.round(10 ** resolutions[i % cols] * newValues[i]) - (i < cols ? 0 : Math.round(10 ** resolutions[i % cols] * newValues[i - cols]));

    // If value is negative take its two's complement ~value, and then left shift by 1 bit

    value = value < 0 ? ~(value << 1) : value << 1;

    // The number is split into 5bit chunks starting from the right
    // Iterate through each chunk in reverse order
    while (value >= 0x20) {
      // OR each value with 0x20 (ie 32)
      // (value & 0x1F) makes sure value is 0-31.
      // we then add 0x3F (ie 63 or '?') to place our chars into the right domain space ie ?@ABC...etc

      output += String.fromCharCode((0x20 | (value & 0x1f)) + 0x3f);
      // move to next 5bit chunk

      value >>= 5;
    }
    output += String.fromCharCode(value + 0x3f);
  }
  return output;
};

const encodeLatLngs = (latLngs: number[][]) => encodeObjectsToPolyline(latLngs, 2, [5, 5]);

export {
  atMapsToGeojson,
  decodedObjectsFromPolyline,
  decodeSegmentLatLng,
  decodeSegmentLngLat,
  decodeLatLngs,
  encodeLatLngs,
  getLineColor,
  getLines,
  getSegments,
  lineOrSegSort,
  pointItemsToGeojson,
  pointItemToLngLat,
  reverseCoord,
  processGroupedSegments,
  getGroupedSegments,
  decodeNDim,
  getIncrStats
};
